import { createAction, handleActions } from "redux-actions";
import * as R from "ramda";
import { EditorState, ContentState } from "draft-js";

/**
 * The refusals that are answered with a dialog instead of a toast.
 *
 * All three mean "we did not read your sheet, and it is not because anything
 * went wrong" -- which is precisely the case a toast handles worst: it states
 * the bad half, offers nothing, and then removes itself before the operator
 * has decided what to do. Every other scan failure stays a toast, because
 * "try again" really is the whole answer.
 *
 * Kept here, next to the state that carries the reason, so the epic that
 * decides whether to toast and the dialog that opens cannot disagree about
 * the list.
 */
export const SCAN_NO_CREDITS = "no_credits";
export const SCAN_SPEND_CAPS = ["proshop_cap", "global_cap"];
export const SCAN_GUIDED_REASONS = [SCAN_NO_CREDITS, ...SCAN_SPEND_CAPS];

export const types = {
  specsheetSetValues: "SPECSHEET_SET_VALUES",
  specsheetUpdateSpecsheets: "SPECSHEET_UPDATE_SPECSHEETS",
  specsheetSetSpecsheets: "SPECSHEET_SET_SPECSHEETS",
  specsheetSetSpecsheet: "SPECSHEET_SET_SPECSHEET",
  specsheetHandleForm: "SPECSHEET_HANDLE_FORM",
  specsheetSaved: "SPECSHEET_SAVED",
  specsheetSelectSpanType: "SPECSHEET_SELECT_SPAN_TYPE",
  specsheetSelect: "SPECSHEET_SELECT",
  specsheetTakePhoto: "SPECSHEET_TAKE_PHOTO",
  specsheetGalleryPhoto: "SPECSHEET_GALLERY_PHOTO",
  specsheetDeletePhoto: "SPECSHEET_DELETE_PHOTO",
  specsheetPhotoDeleted: "SPECSHEET_PHOTO_DELETED",
  specsheetSavePhoto: "SPECSHEET_SAVE_PHOTO",
  specsheetPhotoSaved: "SPECSHEET_PHOTO_SAVED",
  specsheetScanPhoto: "SPECSHEET_SCAN_PHOTO",
  specsheetPhotoScanned: "SPECSHEET_PHOTO_SCANNED",
  specsheetScanPhotoAI: "SPECSHEET_SCAN_PHOTO_AI",
  specsheetPhotoScannedAI: "SPECSHEET_PHOTO_SCANNED_AI",
  specsheetScanPhotoError: "SPECSHEET_SCAN_PHOTO_ERROR",
  specsheetScanPhotoAIError: "SPECSHEET_SCAN_PHOTO_AI_ERROR",
  specsheetCloseScanReview: "SPECSHEET_CLOSE_SCAN_REVIEW",
  specsheetResumeScan: "SPECSHEET_RESUME_SCAN",
  specsheetScanResuming: "SPECSHEET_SCAN_RESUMING",
  specsheetBatchOpen: "SPECSHEET_BATCH_OPEN",
  specsheetBatchClose: "SPECSHEET_BATCH_CLOSE",
  specsheetBatchUpload: "SPECSHEET_BATCH_UPLOAD",
  specsheetBatchUploading: "SPECSHEET_BATCH_UPLOADING",
  specsheetBatchQueued: "SPECSHEET_BATCH_QUEUED",
  specsheetBatchJobs: "SPECSHEET_BATCH_JOBS",
  specsheetBatchError: "SPECSHEET_BATCH_ERROR",
  specsheetBatchReview: "SPECSHEET_BATCH_REVIEW",
  specsheetBatchReviewLoaded: "SPECSHEET_BATCH_REVIEW_LOADED",
  specsheetBatchReviewClose: "SPECSHEET_BATCH_REVIEW_CLOSE",
  specsheetBatchConfirm: "SPECSHEET_BATCH_CONFIRM",
  specsheetBatchConfirmed: "SPECSHEET_BATCH_CONFIRMED",
  specsheetBatchCandidates: "SPECSHEET_BATCH_CANDIDATES",
  specsheetBatchTarget: "SPECSHEET_BATCH_TARGET",
  specsheetBatchDiscard: "SPECSHEET_BATCH_DISCARD",
  specsheetBatchClear: "SPECSHEET_BATCH_CLEAR",
  specsheetCredits: "SPECSHEET_CREDITS",
  specsheetCreditsLoaded: "SPECSHEET_CREDITS_LOADED",
  specsheetSVGClick: "SPECSHEET_SVG_CLICK",
  specsheetSetOpenDropdown: "SPECSHEET_SET_OPEN_DROPDOWN",
  specsheetSVGClickOpen: "SPECSHEET_SVG_CLICK_OPEN",
  specsheetToggleOvalPreview: "SPECSHEET_TOGGLE_OVAL_PREVIEW",
  specsheetCalculateThumbCuts: "SPECSHEET_CALCULATE_THUMB_CUTS",
  specsheetAddThumbCut: "SPECSHEET_ADD_THUMB_CUT",
  specsheetUpdateThumbCut: "SPECSHEET_UPDATE_THUMB_CUT",
  specsheetDeleteThumbCut: "SPECSHEET_DELETE_THUMB_CUT",
  specsheetSetThumbCuts: "SPECSHEET_SET_THUMB_CUTS",
  specsheetSetThumbCutsError: "SPECSHEET_SET_THUMB_CUTS_ERROR",
  specsheetShowThumbcutModal: "SPECSHEET_SHOW_THUMB_CUT_MODAL",
  specsheetAddNew: "SPECSHEET_ADD_NEW",
  specsheetClone: "SPECSHEET_CLONE",
  specSheetRemove: "SPECSHEET_REMOVE",
  specSheetRemoved: "SPECSHEET_REMOVED",
  specSheetNoteState: "SPECSHEET_NOTE_STATE",
  specSheetListRefresh: "SPECSHEET_LIST_REFRESH",
  getCurrentSpecsheetPage: "SPECSHEET_GET_CURRENT_SPECHSHEET",
  setCurrentSpecsheetPage: "SPECSHEET_SET_CURRENT_SPECSHEET",
  specSheetGetAuditRecords: "SPECSHEET_GET_AUDIT_RECORDS",
  specSheetGetAuditRecordsSuccess: "SPECSHEET_GET_AUDIT_RECORDS_SUCCESS",
  specSheetCloseAuditModal: "SPECSHEET_CLOSE_AUDIT_MODAL",
  specSheetSetCurrentSpecsheetAuditRecord: "SPECSHEET_SET_CURRENT_AUDIT_RECORD",
  specSheetSerCurrentThumbcutsAuditRecord:
    "SPECSHEET_SET_CURRENT_THUMBCUTS_AUDIT_RECORD",
  specSheetSendEmail: "SPECSHEET_SEND_EMAIL",
  specSheetSendEmailDone: "SPECSHEET_SEND_EMAIL_DONE",
  specSheetEmailSentError: "SPECSHEET_EMAIL_SENT_ERROR",
  specSheetShareToStats: "SPECSHEET_SHARE_TO_STATS",
  specSheetShareToStatsDone: "SPECSHEET_SHARE_TO_STATS_DONE",
  specSheetShareToStatsError: "SPECSHEET_SHARE_TO_STATS_ERROR",
  specSheetFetchStatsShares: "SPECSHEET_FETCH_STATS_SHARES",
  specSheetFetchStatsSharesDone: "SPECSHEET_FETCH_STATS_SHARES_DONE",
  setShowSharingModal: "SET_SHOW_SHARING_MODAL",
  setShowEmailConfirmModal: "SET_SHOW_EMAIL_CONFIRM_MODAL",
};

export const specsheetSetValues = createAction(types.specsheetSetValues);
export const specsheetUpdateSpecsheets = createAction(
  types.specsheetUpdateSpecsheets,
);
export const specsheetSetSpecsheets = createAction(
  types.specsheetSetSpecsheets,
);
export const specsheetSetSpecsheet = createAction(types.specsheetSetSpecsheet);
export const specsheetHandleForm = createAction(types.specsheetHandleForm);
export const specsheetSaved = createAction(types.specsheetSaved);
export const specsheetSelectSpanType = createAction(
  types.specsheetSelectSpanType,
);
export const specsheetSelect = createAction(types.specsheetSelect);
export const specsheetTakePhoto = createAction(types.specsheetTakePhoto);
export const specsheetGalleryPhoto = createAction(types.specsheetGalleryPhoto);
export const specsheetDeletePhoto = createAction(types.specsheetDeletePhoto);
export const specsheetPhotoDeleted = createAction(types.specsheetPhotoDeleted);
export const specsheetSavePhoto = createAction(types.specsheetSavePhoto);
export const specsheetPhotoSaved = createAction(types.specsheetPhotoSaved);
export const specsheetScanPhoto = createAction(types.specsheetScanPhoto);
export const specsheetPhotoScanned = createAction(types.specsheetPhotoScanned);
export const specsheetScanPhotoAI = createAction(types.specsheetScanPhotoAI);
export const specsheetPhotoScannedAI = createAction(
  types.specsheetPhotoScannedAI,
);
export const specsheetScanPhotoError = createAction(
  types.specsheetScanPhotoError,
);
export const specsheetCloseScanReview = createAction(
  types.specsheetCloseScanReview,
);
export const specsheetResumeScan = createAction(types.specsheetResumeScan);
export const specsheetScanResuming = createAction(types.specsheetScanResuming);
export const specsheetBatchOpen = createAction(types.specsheetBatchOpen);
export const specsheetBatchClose = createAction(types.specsheetBatchClose);
export const specsheetBatchUpload = createAction(types.specsheetBatchUpload);
export const specsheetBatchUploading = createAction(
  types.specsheetBatchUploading,
);
export const specsheetBatchQueued = createAction(types.specsheetBatchQueued);
export const specsheetBatchJobs = createAction(types.specsheetBatchJobs);
export const specsheetBatchError = createAction(types.specsheetBatchError);
export const specsheetCredits = createAction(types.specsheetCredits);
export const specsheetCreditsLoaded = createAction(
  types.specsheetCreditsLoaded,
);
export const specsheetBatchReview = createAction(types.specsheetBatchReview);
export const specsheetBatchReviewLoaded = createAction(
  types.specsheetBatchReviewLoaded,
);
export const specsheetBatchReviewClose = createAction(
  types.specsheetBatchReviewClose,
);
export const specsheetBatchConfirm = createAction(types.specsheetBatchConfirm);
export const specsheetBatchConfirmed = createAction(
  types.specsheetBatchConfirmed,
);
export const specsheetBatchCandidates = createAction(
  types.specsheetBatchCandidates,
);
export const specsheetBatchTarget = createAction(types.specsheetBatchTarget);
export const specsheetBatchDiscard = createAction(
  types.specsheetBatchDiscard,
);
export const specsheetBatchClear = createAction(types.specsheetBatchClear);
export const specsheetScanPhotoAIError = createAction(
  types.specsheetScanPhotoAIError,
);
export const specsheetSVGClick = createAction(types.specsheetSVGClick);
export const specsheetSetOpenDropdown = createAction(
  types.specsheetSetOpenDropdown,
);
export const specsheetSVGClickOpen = createAction(types.specsheetSVGClickOpen);
export const specsheetToggleOvalPreview = createAction(
  types.specsheetToggleOvalPreview,
);
export const specsheetCalculateThumbCuts = createAction(
  types.specsheetCalculateThumbCuts,
);
export const specsheetAddThumbCut = createAction(types.specsheetAddThumbCut);
export const specsheetUpdateThumbCut = createAction(
  types.specsheetUpdateThumbCut,
);
export const specsheetDeleteThumbCut = createAction(
  types.specsheetDeleteThumbCut,
);
export const specsheetSetThumbCuts = createAction(types.specsheetSetThumbCuts);
export const specsheetSetThumbCutsError = createAction(
  types.specsheetSetThumbCutsError,
);
export const specsheetAddNew = createAction(types.specsheetAddNew);
export const specsheetClone = createAction(types.specsheetClone);
export const specSheetRemove = createAction(types.specSheetRemove);
export const specSheetRemoved = createAction(types.specSheetRemoved);
export const specSheetNoteState = createAction(types.specSheetNoteState);
export const specSheetListRefresh = createAction(types.specSheetListRefresh);
export const getCurrentSpecsheetPage = createAction(
  types.getCurrentSpecsheetPage,
);
export const setCurrentSpecsheetPage = createAction(
  types.setCurrentSpecsheetPage,
);
export const specsheetShowThumbcutModal = createAction(
  types.specsheetShowThumbcutModal,
);

export const specSheetGetAuditRecords = createAction(
  types.specSheetGetAuditRecords,
);
export const specSheetGetAuditRecordsSuccess = createAction(
  types.specSheetGetAuditRecordsSuccess,
);
export const specSheetCloseAuditModal = createAction(
  types.specSheetCloseAuditModal,
);
export const specSheetSetCurrentSpecsheetAuditRecord = createAction(
  types.specSheetSetCurrentSpecsheetAuditRecord,
);
export const specSheetSerCurrentThumbcutsAuditRecord = createAction(
  types.specSheetSerCurrentThumbcutsAuditRecord,
);
export const specSheetSendEmail = createAction(types.specSheetSendEmail);
export const specSheetSendEmailDone = createAction(
  types.specSheetSendEmailDone,
);
export const specSheetShareToStats = createAction(
  types.specSheetShareToStats,
);
export const specSheetShareToStatsDone = createAction(
  types.specSheetShareToStatsDone,
);
export const specSheetShareToStatsError = createAction(
  types.specSheetShareToStatsError,
);
export const specSheetFetchStatsShares = createAction(
  types.specSheetFetchStatsShares,
);
export const specSheetFetchStatsSharesDone = createAction(
  types.specSheetFetchStatsSharesDone,
);
export const specSheetEmailSentError = createAction(
  types.specSheetEmailSentError,
);
export const setShowSharingModal = createAction(types.setShowSharingModal);
export const setShowEmailConfirmModal = createAction(
  types.setShowEmailConfirmModal,
);

export const INITIAL_STATE = {
  saving: false,
  currentSpecSheet: {},
  specsheets: [],
  selectedSpan: "full",
  images: [],
  currentSpecSheetIndex: 0,
  openDropdown: "",
  activeTabKey: "1",
  hideRulerBadge: false,
  hideFingerBadge: false,
  hideThumbBadge: false,
  hideNotesBadge: false,
  tabChange: false,
  isOvalPreviewOpen: false,
  thumbCuts: [],
  thumbCutsError: "",
  noteEditorState: EditorState.createEmpty(),
  updatingValues: false,
  showThumbcutModal: false,
  thumbcutSelected: {},
  updatingThumbcut: false,
  savingPhoto: false,
  savingPhotoAI: false,
  // Result of the last AI spec sheet scan. It is parked here rather than
  // written into the form: the operator reviews it first, because a spec
  // sheet read wrong is a ruined ball. needsReview drives the highlight.
  showScanReview: false,
  // The job the review screen is opened at. The values below are what the
  // scan came back with; the screen fetches its own copy from the job, so
  // this is the handoff and they are only what the spinner had to show.
  scanJobId: "",
  scanValues: {},
  batchOpen: false,
  // The batch id is what ties an inbox to its own uploads: /scan/jobs is
  // filtered by it, so a batch started this morning does not reappear on top
  // of one started this afternoon.
  batchId: "",
  batchJobs: [],
  // Sheet credits, as the server last answered. `loaded` is what tells the
  // difference between "nothing left" and "not asked yet" -- showing a
  // paywall because a request has not come back is worse than showing
  // nothing, so the counter waits.
  credits: {
    loaded: false,
    // null means the ledger could not be read. Not zero: a shop that has
    // paid must never be told it has nothing because a table was missing.
    balance: null,
    enforcing: false,
    trialCredits: 0,
    packs: [],
    environment: "",
  },
  batchUploading: 0,
  batchTotal: 0,
  batchError: "",
  batchErrorReason: "",
  // The row being looked at, with the values as read. Kept apart from
  // scanValues on purpose: that one belongs to the sheet open on a bowler's
  // screen, and the inbox is not on a bowler's screen.
  batchReview: null,
  batchReviewLoading: "",
  batchConfirming: "",
  // Job ids already turned into a bowler's sheet. The row stays visible and
  // says so rather than vanishing, so an operator working down a long list can
  // see where they got to.
  batchApplied: {},
  // Bowlers this inbox created. The list in state.bowlers is not updated by a
  // create from here, so without this a second sheet for the same new person
  // would find no match and make a second bowler.
  batchCreated: [],
  // Who the sheet under review might belong to, and who the operator says it
  // does. "" means a new bowler, which is why the default has to be an
  // explicit choice rather than an absent one.
  batchCandidates: [],
  batchSearching: false,
  batchTarget: "",
  batchExactId: "",
  scanNeedsReview: [],
  scanNotFound: [],
  scanNotes: "",
  scanError: "",
  scanErrorReason: "",
  isCalculating: false,
  gettingAuditRecords: false,
  showAuditModal: false,
  specsheetAuditRecords: [],
  currentSpecsheetAuditRecord: null,
  currentSpecsheetAuditRecordIdx: -1,
  thumbcutsAuditRecords: [],
  currentThumbcutsAuditRecord: null,
  currentThumbcutsAuditRecordIdx: -1,
  sendingEmail: false,
  sharingToStats: false,
  // Spectre Stats shares of statsSharesSpecSheetId, newest first
  statsShares: [],
  statsSharesSpecSheetId: null,
  currentSpecsheetFileName: "",
  showSharingModal: false,
  showEmailConfirmModal: false,
};

const rulerFields = [
  "name",
  "hand",
  "ball_grip",
  "f1_span_full",
  "f1_span_cut",
  "f1_span_outer2_cut",
  "f2_span_full",
  "f2_span_cut",
  "f2_span_outer2_cut",
];

const fingerFields = [
  "f1_lat",
  "f1_rev",
  "f1_for",
  "f1_grip_type",
  "f1_size",
  "f1_od",
  "bridge",
  "clt",
  "f2_lat",
  "f2_rev",
  "f2_for",
  "f2_grip_type",
  "f2_size",
  "f2_od",
];

const thumbFields = [
  "thm_flex",
  "thm_offset",
  "thm_lat_l",
  "thm_for",
  "thm_rev",
  "thm_lat_r",
  "thm_insert_type",
  "thm_type",
  "thm_size",
  "thm_round_taper",
  "thm_oval_starting_bit",
  "thm_oval_width",
  "thm_oval_degree",
  "thm_od",
  "thm_oval_taper",
];

const notesFields = ["note"];

export const specsheetReducer = handleActions(
  {
    [types.specsheetSetSpecsheets]: (state, action) => ({
      ...state,
      specsheets: action.payload.specsheets,
    }),
    [types.specsheetUpdateSpecsheets]: (state, action) => ({
      ...state,
      specsheets: action.payload.specsheets,
    }),
    [types.specsheetHandleForm]: (state) => ({
      ...state,
      updatingValues: true,
    }),
    [types.specsheetSetValues]: (state, action) => ({
      ...state,
      currentSpecSheet: {
        ...action.payload.currentSpecSheet,
        thumb_cuts: state.thumbCuts,
      },
      openDropdown: action.payload.noCloseDropdown ? state.openDropdown : "",
      updatingValues: false,
    }),
    [types.specsheetSaved]: (state) => ({
      ...state,
      specsheetSaved: false,
    }),
    [types.specsheetSetSpecsheet]: (state, action) => ({
      ...state,
      currentSpecSheet: action.payload.specsheet,
      thumbCuts: action.payload.specsheet?.thumb_cuts || [],
      currentSpecSheetIndex: 0,
      noteEditorState: EditorState.createWithContent(
        ContentState.createFromText(
          action.payload.specsheet && action.payload.specsheet.note
            ? action.payload.specsheet.note
            : "",
        ),
      ),
    }),
    [types.specsheetSelectSpanType]: (state, action) => ({
      ...state,
      selectedSpan: action.payload.selectedSpan,
    }),
    [types.specsheetPhotoSaved]: (state, action) => {
      const currentSpecSheet = {
        ...state.currentSpecSheet,
        gallery: [...state.currentSpecSheet.gallery, action.payload.photo],
      };

      return {
        ...state,
        currentSpecSheet,
        specsheets: R.update(
          state.currentSpecSheetIndex,
          currentSpecSheet,
          state.specsheets,
        ),
        savingPhoto: false,
      };
    },
    [types.specsheetPhotoScanned]: (state, action) => {
      return {
        ...state,
        savingPhoto: false,
      };
    },
    [types.specsheetPhotoScannedAI]: (state, action) => {
      return {
        ...state,
        savingPhotoAI: false,
        showScanReview: true,
        scanJobId: action.payload.jobId || "",
        scanValues: action.payload.data || {},
        scanNeedsReview: action.payload.needsReview || [],
        scanNotFound: action.payload.notFound || [],
        scanNotes: action.payload.notes || "",
        scanError: "",
        scanErrorReason: "",
      };
    },
    [types.specsheetCloseScanReview]: (state) => ({
      ...state,
      showScanReview: false,
      scanJobId: "",
      scanValues: {},
      scanNeedsReview: [],
      scanNotFound: [],
      scanNotes: "",
    }),
    [types.specsheetScanPhotoAIError]: (state, action) => {
      // Without this the spinner never clears and the screen looks hung.
      return {
        ...state,
        savingPhotoAI: false,
        scanError: (action.payload && action.payload.message) || "",
        // "no_credits" is the refusal the operator can act on: the screen
        // answers it with the packs instead of a toast that disappears.
        scanErrorReason: (action.payload && action.payload.reason) || "",
      };
    },
    [types.specsheetPhotoDeleted]: (state, action) => {
      const currentSpecSheet = {
        ...state.currentSpecSheet,
        gallery: R.remove(
          action.payload.deleteIndex,
          1,
        )(state.currentSpecSheet.gallery),
      };

      return {
        ...state,
        currentSpecSheet,
        specsheets: R.update(
          state.currentSpecSheetIndex,
          currentSpecSheet,
          state.specsheets,
        ),
      };
    },
    [types.specsheetSetOpenDropdown]: (state, action) => ({
      ...state,
      openDropdown: action.payload.openDropdown,
    }),
    [types.specsheetSVGClickOpen]: (state) => ({
      ...state,
      openDropdown: "",
      tabChange: false,
    }),
    [types.specsheetToggleOvalPreview]: (state) => ({
      ...state,
      isOvalPreviewOpen: !state.isOvalPreviewOpen,
    }),
    [types.specsheetSetThumbCuts]: (state, action) => ({
      ...state,
      updatingThumbcut: false,
      currentSpecSheet: {
        ...state.currentSpecSheet,
        thumb_cuts: action.payload.thumbCuts,
      },
      specsheets: R.update(
        state.currentSpecSheetIndex,
        {
          ...state.currentSpecSheet,
          thumb_cuts: action.payload.thumbCuts,
        },
        state.specsheets,
      ),
      thumbCuts: action.payload.thumbCuts,
      isCalculating: false,
    }),
    [types.specsheetSetThumbCutsError]: (state, action) => ({
      ...state,
      thumbCutsError: action.payload.error,
    }),
    [types.specSheetRemoved]: (state, action) => ({
      ...state,
      specsheets: action.payload.specsheets,
    }),
    [types.specSheetNoteState]: (state, action) => ({
      ...state,
      noteEditorState: action.payload.noteEditorState,
    }),
    [types.specsheetAddNew]: (state) => ({
      ...state,
      noteEditorState: EditorState.createEmpty(),
    }),
    [types.getCurrentSpecsheetPage]: (state) => ({
      ...state,
    }),
    [types.setCurrentSpecsheetPage]: (state, action) => ({
      ...state,
      currentSpecSheetPage: action.payload.currentSpecSheetPage,
    }),
    [types.specsheetShowThumbcutModal]: (state, action) => ({
      ...state,
      showThumbcutModal: action.payload.show,
      thumbcutSelected: action.payload.thumbcut,
    }),
    [types.specsheetUpdateThumbCut]: (state, action) => ({
      ...state,
      updatingThumbcut: true,
    }),
    [types.specsheetSavePhoto]: (state) => ({
      ...state,
      savingPhoto: true,
    }),
    [types.specsheetScanPhoto]: (state) => ({
      ...state,
      savingPhoto: true,
    }),
    [types.specsheetBatchOpen]: (state, action) => ({
      ...state,
      batchOpen: true,
      batchId: action.payload?.batchId || state.batchId,
      batchError: "",
      batchErrorReason: "",
    }),
    [types.specsheetBatchClose]: (state) => ({
      ...state,
      batchOpen: false,
      batchError: "",
      batchErrorReason: "",
    }),
    [types.specsheetBatchUploading]: (state, action) => ({
      ...state,
      batchId: action.payload.batchId,
      batchUploading: action.payload.done,
      batchTotal: action.payload.total,
      batchError: "",
      batchErrorReason: "",
    }),
    [types.specsheetBatchQueued]: (state) => ({
      ...state,
      batchUploading: 0,
      batchTotal: 0,
    }),
    [types.specsheetCreditsLoaded]: (state, action) => {
      const balance =
        action.payload.balance === null || action.payload.balance === undefined
          ? null
          : Number(action.payload.balance);

      // Sheets arriving is the answer to "you have no sheets", so the
      // refusal goes with them. It used to be left standing: the counter in
      // the header changed to the number just bought while the panel under
      // it still said there were none, and the only way out was a reload.
      const refusalAnswered = balance !== null && balance > 0;

      return {
        ...state,
        batchError:
          refusalAnswered && state.batchErrorReason === "no_credits"
            ? ""
            : state.batchError,
        batchErrorReason:
          refusalAnswered && state.batchErrorReason === "no_credits"
            ? ""
            : state.batchErrorReason,
        // The same refusal on a bowler's own sheet, which is a dialog rather
        // than a panel: clearing the reason is what closes it.
        scanError:
          refusalAnswered && state.scanErrorReason === "no_credits"
            ? ""
            : state.scanError,
        scanErrorReason:
          refusalAnswered && state.scanErrorReason === "no_credits"
            ? ""
            : state.scanErrorReason,
        credits: {
          loaded: true,
          balance,
          enforcing: !!action.payload.enforcing,
          trialCredits: Number(action.payload.trialCredits || 0),
          packs: action.payload.packs || [],
          environment: action.payload.environment || "",
        },
      };
    },
    [types.specsheetBatchJobs]: (state, action) => ({
      ...state,
      batchJobs: action.payload.jobs || [],
    }),
    [types.specsheetBatchReview]: (state, action) => ({
      ...state,
      batchReviewLoading: action.payload.jobId,
      batchError: "",
      batchErrorReason: "",
      batchCandidates: [],
      batchSearching: true,
      batchTarget: "",
      batchExactId: "",
    }),
    [types.specsheetBatchCandidates]: (state, action) => ({
      ...state,
      batchSearching: false,
      batchCandidates: action.payload.candidates || [],
      batchExactId: action.payload.exactId || "",
      // An exact match is preselected; anything less is offered but never
      // chosen for the operator. Attaching a sheet to the wrong person is the
      // mistake nobody catches later.
      batchTarget: action.payload.exactId || "",
    }),
    [types.specsheetBatchTarget]: (state, action) => ({
      ...state,
      batchTarget: action.payload.customerId || "",
    }),
    // Both drop the row now rather than after a round trip: the operator has
    // already decided, and a list that argues about it feels broken. The epic
    // re-reads the inbox if the server disagrees.
    [types.specsheetBatchDiscard]: (state, action) => ({
      ...state,
      batchJobs: state.batchJobs.filter(
        (j) => j.jobId !== action.payload.jobId,
      ),
    }),
    [types.specsheetBatchClear]: (state) => ({
      ...state,
      batchJobs: [],
      batchReview: null,
      batchReviewLoading: "",
    }),
    [types.specsheetBatchReviewLoaded]: (state, action) => ({
      ...state,
      batchReviewLoading: "",
      batchReview: action.payload,
    }),
    [types.specsheetBatchReviewClose]: (state) => ({
      ...state,
      batchReview: null,
      batchReviewLoading: "",
      batchCandidates: [],
      batchSearching: false,
      batchTarget: "",
      batchExactId: "",
    }),
    [types.specsheetBatchConfirm]: (state, action) => ({
      ...state,
      batchConfirming: action.payload.jobId,
    }),
    [types.specsheetBatchConfirmed]: (state, action) => ({
      ...state,
      batchConfirming: "",
      batchReview: null,
      batchApplied: {
        ...state.batchApplied,
        [action.payload.jobId]: action.payload.customerId,
      },
      batchCreated: action.payload.customer
        ? [...state.batchCreated, action.payload.customer]
        : state.batchCreated,
    }),
    [types.specsheetBatchError]: (state, action) => ({
      ...state,
      batchUploading: 0,
      batchTotal: 0,
      batchConfirming: "",
      batchReviewLoading: "",
      batchError: action.payload?.message || "",
      // Why it was refused, when the server said. The screen shows the packs
      // for "no_credits" and a plain line for anything else.
      batchErrorReason: action.payload?.reason || "",
    }),
    [types.specsheetScanResuming]: (state) => ({
      // Only once the epic has actually found a job in flight. Setting this on
      // specsheetResumeScan itself left the spinner on for ever whenever there
      // was nothing to resume, which is every ordinary page load.
      ...state,
      savingPhotoAI: true,
    }),
    [types.specsheetScanPhotoAI]: (state) => ({
      ...state,
      savingPhotoAI: true,
      showScanReview: false,
      scanValues: {},
      scanNeedsReview: [],
      scanNotFound: [],
      scanNotes: "",
      scanError: "",
      scanErrorReason: "",
    }),
    [types.specsheetCalculateThumbCuts]: (state) => ({
      ...state,
      isCalculating: true,
    }),
    [types.specSheetGetAuditRecords]: (state) => ({
      ...state,
      gettingAuditRecords: true,
      showAuditModal: true,
      currentSpecsheetAuditRecordIdx: -1,
      currentSpecsheetAuditRecord: null,
      currentThumbcutsAuditRecordIdx: -1,
      currentThumbcutsAuditRecord: null,
    }),
    [types.specSheetGetAuditRecordsSuccess]: (state, action) => ({
      ...state,
      specsheetAuditRecords: action.payload.specsheetAuditRecords,
      currentSpecsheetAuditRecordIdx:
        action.payload.specsheetAuditRecords.length - 1,
      currentSpecsheetAuditRecord:
        action.payload.specsheetAuditRecords[
          action.payload.specsheetAuditRecords.length - 1
        ],
      thumbcutsAuditRecords: action.payload.thumbcutsAuditRecords,
      currentThumbcutsAuditRecordIdx:
        action.payload.thumbcutsAuditRecords.length - 1,
      currentThumbcutsAuditRecord:
        action.payload.thumbcutsAuditRecords[
          action.payload.thumbcutsAuditRecords.length - 1
        ],
      gettingAuditRecords: false,
      showAuditModal: true,
    }),
    [types.specSheetCloseAuditModal]: (state) => ({
      ...state,
      showAuditModal: false,
      currentSpecsheetAuditRecord: null,
      currentSpecsheetAuditRecordIdx: -1,
      currentThumbcutsAuditRecord: null,
      currentThumbcutsAuditRecordIdx: -1,
    }),
    [types.specSheetSetCurrentSpecsheetAuditRecord]: (state, action) => ({
      ...state,
      currentSpecsheetAuditRecordIdx:
        action.payload.currentSpecsheetAuditRecordIdx,
      currentSpecsheetAuditRecord:
        state.specsheetAuditRecords[
          action.payload.currentSpecsheetAuditRecordIdx
        ],
    }),
    [types.specSheetSerCurrentThumbcutsAuditRecord]: (state, action) => ({
      ...state,
      currentThumbcutsAuditRecordIdx:
        action.payload.currentThumbcutsAuditRecordIdx,
      currentThumbcutsAuditRecord:
        state.thumbcutsAuditRecords[
          action.payload.currentThumbcutsAuditRecordIdx
        ],
    }),
    [types.specSheetSendEmail]: (state) => ({
      ...state,
      sendingEmail: true,
    }),
    [types.specSheetSendEmailDone]: (state) => ({
      ...state,
      sendingEmail: false,
    }),
    [types.specSheetEmailSentError]: (state, action) => ({
      ...state,
      sendingEmail: false,
    }),
    [types.specSheetShareToStats]: (state) => ({
      ...state,
      sharingToStats: true,
    }),
    [types.specSheetShareToStatsDone]: (state) => ({
      ...state,
      sharingToStats: false,
    }),
    [types.specSheetShareToStatsError]: (state) => ({
      ...state,
      sharingToStats: false,
    }),
    [types.specSheetFetchStatsShares]: (state, action) => ({
      ...state,
      // Never show another sheet's status while this one loads.
      statsShares:
        state.statsSharesSpecSheetId === action.payload.specSheetId
          ? state.statsShares
          : [],
      statsSharesSpecSheetId: action.payload.specSheetId,
    }),
    [types.specSheetFetchStatsSharesDone]: (state, action) =>
      action.payload.specSheetId === state.statsSharesSpecSheetId
        ? { ...state, statsShares: action.payload.shares }
        : state,
    [types.setShowSharingModal]: (state, action) => ({
      ...state,
      showSharingModal: action.payload.showSharingModal,
    }),
    [types.setShowEmailConfirmModal]: (state, action) => ({
      ...state,
      showEmailConfirmModal: action.payload.showEmailConfirmModal,
    }),
  },
  INITIAL_STATE,
);
